import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { useI18n } from "@/context/I18nContext";
import { API } from "@/context/AuthContext";

/**
 * Generic admin panel for any "simple" V2 catalog (brands, suppliers,
 * sizes, colors, materials, units_of_measure, item_types,
 * decoration_methods, logo_locations).
 *
 * All these catalogs share the same shape on the wire — {id, name,
 * is_active, created_at, updated_at} — and the same REST surface. We get
 * 9 fully-featured admin panels out of one component instead of nine
 * copy-pasted files.
 *
 * Props
 * - resource: API path segment (e.g. "brands", "decoration-methods")
 * - title: heading to render (e.g. "Marcas")
 * - testIdPrefix: stable prefix used for data-testid attrs ("brand", "size", …)
 * - onChanged: optional callback fired after any successful mutation,
 *   useful for refreshing dependent lists in other panels.
 */
export default function SimpleCatalogPanel({
  resource,
  title,
  testIdPrefix,
  onChanged,
}) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/${resource}`, {
        withCredentials: true,
      });
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, [resource]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing({});
    setName("");
    setActive(true);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setName(item.name || "");
    setActive(!!item.is_active);
    setOpen(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t("name_required"));
      return;
    }
    setSaving(true);
    try {
      const payload = { name: trimmed, is_active: active };
      if (editing?.id) {
        await axios.put(`${API}/${resource}/${editing.id}`, payload, {
          withCredentials: true,
        });
      } else {
        await axios.post(`${API}/${resource}`, payload, {
          withCredentials: true,
        });
      }
      toast.success(t("saved_ok"));
      setOpen(false);
      setEditing(null);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item) => {
    try {
      await axios.patch(
        `${API}/${resource}/${item.id}/toggle-active`,
        null,
        { withCredentials: true },
      );
      toast.success(t("state_changed_ok"));
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const filtered = items.filter((c) => {
    if (statusFilter === "active") return c.is_active;
    if (statusFilter === "inactive") return !c.is_active;
    return true;
  });

  return (
    <div data-testid={`${testIdPrefix}-list`} className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-3">
          <div>
            <label className="label-overline block mb-1">{t("status")}</label>
            <select
              data-testid={`${testIdPrefix}-filter-status`}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-[var(--border)] px-3 py-2 font-mono-tech text-sm bg-white"
            >
              <option value="all">{t("all_statuses")}</option>
              <option value="active">{t("active_only")}</option>
              <option value="inactive">{t("inactive_only")}</option>
            </select>
          </div>
        </div>
        <button
          data-testid={`${testIdPrefix}-new-btn`}
          onClick={openCreate}
          className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
        >
          + {t("new_record")}
        </button>
      </div>

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid={`${testIdPrefix}-empty`}
          className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]"
        >
          {t("no_records")}
        </div>
      ) : (
        <div className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]">
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`${testIdPrefix}-row-${c.id}`}
              className={`grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-[var(--bg)] ${
                c.is_active ? "" : "opacity-60"
              }`}
            >
              <div className="col-span-12 sm:col-span-7">
                <div className="font-display text-lg font-bold tracking-tight">
                  {c.name}
                </div>
              </div>
              <div className="col-span-6 sm:col-span-2">
                <span
                  className={`font-mono-tech text-[0.65rem] tracking-wider px-2 py-1 ${
                    c.is_active
                      ? "bg-black text-white"
                      : "border border-[var(--border)] text-[var(--text-secondary)]"
                  }`}
                >
                  {c.is_active ? t("active") : t("inactive")}
                </span>
              </div>
              <div className="col-span-6 sm:col-span-3 flex justify-end gap-2">
                <button
                  data-testid={`${testIdPrefix}-edit-${c.id}`}
                  onClick={() => openEdit(c)}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm font-mono-tech hover:bg-[var(--text-primary)] hover:text-white transition-colors"
                >
                  {t("edit")}
                </button>
                <button
                  data-testid={`${testIdPrefix}-toggle-${c.id}`}
                  onClick={() => toggle(c)}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm font-mono-tech hover:bg-[var(--text-primary)] hover:text-white transition-colors"
                >
                  {c.is_active ? t("deactivate") : t("activate")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing?.id ? `${t("edit")} — ${title}` : `${t("new_record")} — ${title}`}
        testId={`${testIdPrefix}-modal`}
      >
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-overline block mb-1">{t("name")}</label>
            <input
              data-testid={`${testIdPrefix}-form-name`}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-[var(--border)] px-3 py-2 font-sans"
            />
          </div>
          <label className="flex items-center gap-2 select-none cursor-pointer">
            <input
              data-testid={`${testIdPrefix}-form-active`}
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            <span className="font-mono-tech text-sm">{t("active")}</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setEditing(null);
              }}
              className="border border-[var(--border)] px-4 py-2 font-mono-tech text-sm"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              data-testid={`${testIdPrefix}-form-submit`}
              disabled={saving}
              className="bg-[var(--text-primary)] text-white px-5 py-2 font-display font-bold tracking-wide disabled:opacity-60"
            >
              {saving ? "…" : t("save")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
